import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import EmailCapture from '../components/EmailCapture';

const GUIDES = [
  {
    id: 'roth-ira',
    icon: '🏦',
    title: 'How to Open a Roth IRA',
    tag: 'Retirement',
    tagColor: '#22c55e',
    summary: 'A Roth IRA lets you invest money after taxes — and all growth and withdrawals in retirement are completely tax-free. One of the most powerful tools available to everyday Americans.',
    bestFor: 'Anyone with earned income who expects to be in a higher tax bracket in retirement. Especially powerful for younger people who have decades for their money to grow.',
    limits: '2024 contribution limit: $7,000/year ($8,000 if you\'re 50+). Income limits apply — in 2024, phase-out begins at $146,000 (single) or $230,000 (married).',
    steps: [
      { title: 'Confirm you\'re eligible', desc: 'You need earned income (wages, salary, freelance pay). Investment income doesn\'t count. Check the IRS income limits for your filing status.' },
      { title: 'Choose where to open it', desc: 'You can open a Roth IRA at any brokerage firm that offers them — most major online brokerages do. Look for no account minimums and no annual fees. Many offer online applications that take 10–15 minutes.' },
      { title: 'Apply online', desc: 'You\'ll need: your Social Security number, a government-issued ID, your bank account info (for funding), and your employment information. The application is typically online and takes under 20 minutes.' },
      { title: 'Fund the account', desc: 'Link your bank account and transfer money in. You can start with as little as $1 at many brokerages. You have until the tax deadline (typically April 15) to contribute for the prior year.' },
      { title: 'Choose your investments', desc: 'Once money is in the account, you must choose what to invest in — the money sitting in cash earns almost nothing. Many beginners start with a low-cost index fund that tracks the S&P 500 or a "target-date" retirement fund.' },
      { title: 'Set up automatic contributions', desc: 'Automate a monthly transfer so you contribute consistently without thinking about it. Even $100/month adds up significantly over decades thanks to compound growth.' },
    ],
    keyFacts: [
      'You can withdraw your contributions (not earnings) at any time without penalty',
      'Earnings are tax-free after age 59½ and 5 years of account age',
      'No required minimum distributions — you\'re never forced to take money out',
      'You can have both a Roth IRA and a 401(k) at the same time',
    ],
  },
  {
    id: 'traditional-ira',
    icon: '📋',
    title: 'How to Open a Traditional IRA',
    tag: 'Retirement',
    tagColor: '#22c55e',
    summary: 'A Traditional IRA lets you contribute pre-tax money, reducing your taxable income now. You pay taxes when you withdraw in retirement. Great if you expect to be in a lower tax bracket later.',
    bestFor: 'People who want a tax deduction now and expect lower income in retirement. Also useful when you exceed Roth IRA income limits.',
    limits: '2026 contribution limit: $7,500/year ($8,500 if 50+). Deductibility depends on income and whether you have a workplace retirement plan.',
    steps: [
      { title: 'Decide if a Traditional IRA is right for you', desc: 'If you expect your tax rate to be lower in retirement than it is today, a Traditional IRA often makes more sense than a Roth. If you\'re unsure, a tax professional can help you think it through.' },
      { title: 'Choose a brokerage', desc: 'Same as a Roth IRA — most major online brokerages offer Traditional IRAs. Look for no annual fees, low (or no) account minimums, and a good selection of low-cost index funds.' },
      { title: 'Complete the online application', desc: 'You\'ll need your Social Security number, a government-issued ID, bank account information, and basic personal details. Most applications take 10–20 minutes.' },
      { title: 'Fund the account', desc: 'Link your bank and transfer money in. You have until the tax filing deadline to contribute for the prior year. Consider maxing out if you can — the full $7,000 reduces your taxable income dollar for dollar (if you qualify for the deduction).' },
      { title: 'Invest the funds', desc: 'Cash sitting in the account earns almost nothing. Choose investments — many beginners start with an S&P 500 index fund or a target-date fund aligned with their expected retirement year.' },
      { title: 'Track your contributions for taxes', desc: 'Keep records of your contributions each year. Your brokerage will send you a Form 5498 showing your contributions — you\'ll need this when filing your taxes to claim the deduction.' },
    ],
    keyFacts: [
      'Contributions may be tax-deductible depending on your income and whether you have a 401k',
      'Required minimum distributions (RMDs) start at age 73 — you must start taking money out',
      'Early withdrawals before age 59½ trigger a 10% penalty plus income taxes',
      'You can convert a Traditional IRA to a Roth IRA (called a "Roth conversion") — taxes apply at conversion',
    ],
  },
  {
    id: '401k',
    icon: '💼',
    title: 'How to Enroll in Your 401(k)',
    tag: 'Retirement',
    tagColor: '#22c55e',
    summary: 'A 401(k) is a retirement account offered by your employer. Contributions come out of your paycheck before taxes. Many employers match a portion of what you put in — that\'s free money.',
    bestFor: 'Anyone with a job that offers a 401(k) plan. The employer match alone makes this one of the best investments available — most financial experts say contributing at least enough to get the full match should be a top priority.',
    limits: '2026 employee contribution limit: $24,500/year ($31,000 if 50+). Total limit including employer contributions: $70,000.',
    steps: [
      { title: 'Find out if your employer offers a 401(k)', desc: 'Ask your HR department or check your employee benefits portal. Many employers automatically enroll new employees, but some require you to opt in.' },
      { title: 'Learn your employer\'s match policy', desc: 'Ask HR: "What is the employer match on the 401(k)?" A common structure is "50% match up to 6% of salary" — meaning if you contribute 6%, your employer adds another 3%. Not getting the full match is leaving free money on the table.' },
      { title: 'Enroll through HR or your benefits portal', desc: 'Log into your company\'s HR system or benefits portal (or fill out a paper form) to officially enroll. You\'ll choose what percentage of your paycheck to contribute.' },
      { title: 'Choose your contribution amount', desc: 'At minimum, contribute enough to get the full employer match. From there, try to increase contributions over time — even 1% more per year adds up significantly by retirement.' },
      { title: 'Select your investments', desc: 'Your 401(k) will offer a menu of funds to choose from. Look for low-cost index funds (low "expense ratios"). Many plans offer a "target-date fund" — pick the one closest to your expected retirement year for a simple, diversified option.' },
      { title: 'Review periodically', desc: 'Check your 401(k) once or twice a year. As you get older and closer to retirement, you may want to shift to more conservative investments. Many target-date funds do this automatically.' },
    ],
    keyFacts: [
      'Traditional 401(k): pre-tax contributions, pay taxes in retirement',
      'Roth 401(k) (if offered): after-tax contributions, tax-free withdrawals in retirement',
      'If you leave your job, you can roll your 401(k) into an IRA or your new employer\'s 401(k)',
      'Early withdrawals before 59½ trigger a 10% penalty plus income taxes — avoid if possible',
    ],
  },
  {
    id: 'brokerage',
    icon: '📈',
    title: 'How to Open a Brokerage Account',
    tag: 'Investing',
    tagColor: '#38bdf8',
    summary: 'A brokerage account lets you buy and sell stocks, ETFs, mutual funds, and other investments. Unlike a Roth IRA or 401(k), there are no contribution limits or tax advantages — but also no restrictions on when you can take your money out.',
    bestFor: 'Anyone who has maxed out their retirement accounts, wants to invest beyond retirement savings, or wants to invest with money they might need before retirement age.',
    limits: 'No contribution limits. No income restrictions. You can deposit and withdraw freely. Profits are subject to capital gains tax.',
    steps: [
      { title: 'Choose a brokerage firm', desc: 'Most major online brokerages offer commission-free trades, no account minimums, and easy-to-use apps. Look for: $0 commissions, no account minimums, a good mobile app, and access to the investments you want (stocks, ETFs, etc.).' },
      { title: 'Start your online application', desc: 'Go to the brokerage\'s website and click "Open an Account." You\'ll choose the type: Individual Brokerage Account (most common for personal investing).' },
      { title: 'Provide your information', desc: 'You\'ll need: Social Security number, government-issued ID, your home address and phone number, employment information, and answers to financial questions (income, net worth, investment experience). This is required by law (Know Your Customer rules).' },
      { title: 'Wait for approval', desc: 'Most accounts are approved instantly or within 1–2 business days. You\'ll receive a confirmation email.' },
      { title: 'Fund the account', desc: 'Link your bank account via routing and account number. Transfer money in. Most brokerages allow you to start investing immediately with a small deposit — sometimes as little as $1.' },
      { title: 'Start investing', desc: 'Search for the stock or ETF you want to buy, enter the amount, and place your order. For beginners, broad market ETFs (funds that track the S&P 500, for example) are a common starting point due to built-in diversification.' },
    ],
    keyFacts: [
      'Profits from investments held over 1 year are taxed at the long-term capital gains rate (0–20%)',
      'Profits from investments held under 1 year are taxed as ordinary income (your regular tax rate)',
      'You can withdraw your money anytime with no penalties',
      'Brokerage accounts are insured by SIPC up to $500,000 (protects against broker failure, not investment losses)',
    ],
  },
  {
    id: 'hysa',
    icon: '💰',
    title: 'How to Open a High-Yield Savings Account',
    tag: 'Savings',
    tagColor: '#f59e0b',
    summary: 'A High-Yield Savings Account (HYSA) is a savings account that pays significantly more interest than a typical bank savings account. Great for emergency funds and short-term savings goals. Currently many pay 3.5–4.1% APY vs. the national average of ~0.5%.',
    bestFor: 'Emergency funds, short-term savings goals (1–3 years), or any money you don\'t want to invest in the stock market but want to grow safely.',
    limits: 'No contribution limits. FDIC-insured up to $250,000 per depositor. Some may have minimum balances to earn the advertised rate.',
    steps: [
      { title: 'Compare current rates', desc: 'Rates vary between institutions. Online banks and credit unions typically offer the highest rates. Check financial comparison sites (like Bankrate or NerdWallet) to see current top rates — they change frequently.' },
      { title: 'Choose an institution', desc: 'Online banks often offer the best rates because they have lower overhead than traditional banks. Make sure the institution is FDIC-insured (banks) or NCUA-insured (credit unions) — this protects your money up to $250,000.' },
      { title: 'Apply online', desc: 'Most HYSA applications take 5–10 minutes. You\'ll need: your Social Security number, a government-issued ID, your current bank information (to fund the account), and your address.' },
      { title: 'Fund the account', desc: 'Link your existing bank account and transfer money over. Transfers typically take 1–3 business days. Some accounts let you set up direct deposit from your employer.' },
      { title: 'Set up automatic transfers', desc: 'Automate a monthly transfer from your checking account to build your savings consistently. Even a small automatic transfer creates a savings habit.' },
    ],
    keyFacts: [
      'APY (Annual Percentage Yield) is the actual annual return including compound interest',
      'Rates are variable — the rate you open with can change as the Fed adjusts rates',
      'FDIC insurance protects up to $250,000 per depositor, per institution',
      'Not meant for long-term wealth building — the stock market has historically outperformed savings account rates over the long run',
    ],
  },
  {
    id: 'hsa',
    icon: '🏥',
    title: 'How to Open a Health Savings Account (HSA)',
    tag: 'Healthcare + Investing',
    tagColor: '#a78bfa',
    summary: 'An HSA is a triple-tax-advantaged account: contributions are pre-tax, growth is tax-free, and withdrawals for qualified medical expenses are tax-free. Many consider it the most tax-efficient account available.',
    bestFor: 'Anyone enrolled in a High-Deductible Health Plan (HDHP). If you\'re healthy and can afford to pay small medical costs out of pocket, you can invest HSA funds and let them grow tax-free for retirement.',
    limits: '2026 contribution limit: $4,400 (individual) or $8,750 (family). Must be enrolled in a qualifying high-deductible health plan (HDHP).',
    steps: [
      { title: 'Confirm you have a qualifying health plan', desc: 'To contribute to an HSA, you must be enrolled in a High-Deductible Health Plan (HDHP). Check with your employer or insurance provider. In 2026, an HDHP has a minimum deductible of $1,650 (individual) or $3,300 (family).' },
      { title: 'Check if your employer offers one', desc: 'Many employers who offer HDHPs also offer an HSA — sometimes with employer contributions. Check your benefits portal first. If not, you can open one independently.' },
      { title: 'Open the account', desc: 'You can open an HSA through your employer\'s benefits, many banks, credit unions, or specialized HSA providers. Look for low fees and investment options if you plan to invest rather than just spend.' },
      { title: 'Fund the account', desc: 'Contribute via payroll deduction (pre-tax) through your employer, or contribute directly and claim the deduction on your taxes. Both methods work — payroll deduction also avoids Social Security and Medicare taxes.' },
      { title: 'Decide: spend or invest', desc: 'You can use the money for current medical expenses (tax-free), OR invest it for long-term growth. Many people pay small medical costs out of pocket and let the HSA grow — it becomes a powerful retirement healthcare fund.' },
      { title: 'Save your medical receipts', desc: 'There is no time limit on reimbursements from an HSA. Save your medical receipts and reimburse yourself years later — even in retirement. This is a powerful tax strategy.' },
    ],
    keyFacts: [
      'Triple tax advantage: pre-tax contributions, tax-free growth, tax-free withdrawals for medical expenses',
      'After age 65, you can withdraw for any reason (like a Traditional IRA) — medical use stays tax-free, non-medical is taxed as income',
      'Unused funds roll over year after year — no "use it or lose it" rule',
      'You cannot contribute to an HSA if you\'re enrolled in Medicare',
    ],
  },
  {
    id: 'index-funds',
    icon: '📊',
    title: 'How to Start Investing in Index Funds',
    tag: 'Investing',
    tagColor: '#38bdf8',
    summary: 'An index fund is a type of investment that automatically tracks a market index — like the S&P 500 — by holding all (or most) of the stocks in it. Instead of trying to pick winners, you own a small piece of the whole market.',
    bestFor: 'Beginners and experienced investors alike. Warren Buffett has publicly said that for most people, investing in a low-cost S&P 500 index fund is the best strategy. Low fees, automatic diversification, historically strong returns.',
    limits: 'No contribution limits in a brokerage account. Many funds can be purchased for as little as $1. Can be held inside a Roth IRA, 401(k), or regular brokerage account.',
    steps: [
      { title: 'Open an account first', desc: 'You need a brokerage account (or Roth IRA, or 401k) to hold index funds. If you don\'t have one, start with the guides above. Most major brokerages offer index funds.' },
      { title: 'Understand the two main types', desc: 'Index Mutual Funds: purchased at end-of-day price, often through your brokerage or employer plan. Index ETFs (Exchange-Traded Funds): purchased throughout the day like a stock. Both track the same indices — ETFs are more flexible, mutual funds can be easier to automate.' },
      { title: 'Choose which index to track', desc: 'S&P 500: 500 largest US companies — the most common starting point. Total Market: all US publicly traded companies. International: non-US companies for global diversification. Bond index: lower risk, lower return — more appropriate as you approach retirement.' },
      { title: 'Check the expense ratio', desc: 'The expense ratio is the annual fee as a percentage of your investment. Look for funds under 0.10% (10 basis points). This is a critical factor — a 1% fee vs. a 0.03% fee can cost you tens of thousands of dollars over decades.' },
      { title: 'Buy shares', desc: 'Search for the fund by ticker symbol in your brokerage account, enter the dollar amount you want to invest, and confirm the purchase. That\'s it — you now own a tiny piece of hundreds of companies.' },
      { title: 'Automate and stay the course', desc: 'Set up automatic monthly investments. Don\'t panic and sell when the market drops — staying invested through downturns is historically how long-term wealth is built. Time in the market beats timing the market.' },
    ],
    keyFacts: [
      'The S&P 500 has historically returned ~10% per year on average (before inflation) over long periods — though returns are not guaranteed',
      'Diversification: owning an S&P 500 index fund means owning a tiny piece of 500 companies',
      'Expense ratios matter enormously over time — a 1% higher fee can cost $100,000+ over a 30-year period on a $100k investment',
      'You cannot "beat the market" by owning the market — and most actively managed funds underperform index funds over the long run (per S&P\'s SPIVA report)',
    ],
  },
];

function GuideCard({ guide, isOpen, onToggle }) {
  const tagStyle = { ...s.tag, background: guide.tagColor + '18', color: guide.tagColor, borderColor: guide.tagColor + '40' };
  return (
    <div style={{ ...s.card, ...(isOpen ? s.cardOpen : {}) }}>
      {/* Header */}
      <div style={s.cardHeader} onClick={onToggle}>
        <div style={s.cardLeft}>
          <span style={s.cardIcon}>{guide.icon}</span>
          <div>
            <div style={s.cardTitle}>{guide.title}</div>
            <span style={tagStyle}>{guide.tag}</span>
          </div>
        </div>
        <div style={s.toggle}>{isOpen ? '▲' : '▼'}</div>
      </div>

      {/* Summary always visible */}
      <p style={s.cardSummary}>{guide.summary}</p>

      {/* Expanded content */}
      {isOpen && (
        <div style={s.expanded}>
          <div style={s.infoRow}>
            <div style={s.infoBox}>
              <div style={s.infoLabel}>👤 Best for</div>
              <p style={s.infoText}>{guide.bestFor}</p>
            </div>
            <div style={s.infoBox}>
              <div style={s.infoLabel}>📏 Limits & rules</div>
              <p style={s.infoText}>{guide.limits}</p>
            </div>
          </div>

          <div style={s.stepsSection}>
            <div style={s.stepsTitle}>Step-by-step guide</div>
            {guide.steps.map((step, i) => (
              <div key={i} style={s.step}>
                <div style={s.stepNum}>{i + 1}</div>
                <div style={s.stepBody}>
                  <div style={s.stepTitle}>{step.title}</div>
                  <p style={s.stepDesc}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={s.keyFacts}>
            <div style={s.keyFactsTitle}>💡 Key things to know</div>
            {guide.keyFacts.map((fact, i) => (
              <div key={i} style={s.fact}>
                <span style={s.factDot}>•</span>
                <span style={s.factText}>{fact}</span>
              </div>
            ))}
          </div>

          <p style={s.disclaimer}>
            ⚠️ This is general financial education — not personalized advice. Tax laws and contribution limits change annually. Verify current limits on IRS.gov or consult a tax professional for your specific situation.
          </p>
        </div>
      )}
    </div>
  );
}

      {/* Email capture */}
      <div style={{ marginTop: '48px', marginBottom: '24px' }}>
        <EmailCapture variant="card" source="howto" title="Get smarter about money — free." subtitle="The KnowFi Daily Brief: plain English market news every morning." />
      </div>

export default function HowTo() {
  const [openId, setOpenId] = useState('roth-ira'); // open first by default
  const [filter, setFilter] = useState('all');

  const tags = ['all', 'Retirement', 'Investing', 'Savings', 'Healthcare + Investing'];
  const filtered = filter === 'all' ? GUIDES : GUIDES.filter(g => g.tag === filter);

  return (
    <div style={s.page}>
      <SEO
        title="How to Open a Roth IRA, 401k, Brokerage Account & More"
        description="Step-by-step guides for opening a Roth IRA, Traditional IRA, 401k, brokerage account, HYSA, HSA, and index fund account. No jargon, no fluff."
        path="/howto"
        keywords="how to open Roth IRA, how to open 401k, how to open brokerage account, HYSA, HSA, index fund guide"
      />
      <div style={s.glow} />
      <div style={s.inner}>

        {/* Header */}
        <div style={s.header}>
          <div style={s.eyebrow}>🎓 Step-by-step guides</div>
          <h1 style={s.title}>How to Open the Right Accounts</h1>
          <p style={s.sub}>
            Most people never learned this stuff. Here's exactly how to open the accounts that help you build wealth — no prior knowledge needed, no specific company promoted.
          </p>
        </div>

        {/* Filter tabs */}
        <div style={s.filters}>
          {tags.map(t => (
            <button key={t} style={{ ...s.filterBtn, ...(filter === t ? s.filterActive : {}) }} onClick={() => { setFilter(t); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
              {t === 'all' ? 'All Guides' : t}
            </button>
          ))}
        </div>

        {/* Guide cards */}
        <div style={s.guides}>
          {filtered.map(guide => (
            <GuideCard
              key={guide.id}
              guide={guide}
              isOpen={openId === guide.id}
              onToggle={() => setOpenId(openId === guide.id ? null : guide.id)}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div style={s.cta}>
          <div style={s.ctaLeft}>
            <strong style={{ color: '#f1f5f9', fontSize: '16px' }}>Still have questions?</strong>
            <p style={s.ctaDesc}>Ask our AI Finance Coach anything — it explains concepts in plain English, no judgment.</p>
          </div>
          <div style={s.ctaBtns}>
            <Link to="/ask" style={s.ctaBtn}>🤖 Ask the AI</Link>
            <Link to="/gameplan" style={s.ctaBtnOutline}>🎯 Build My Plan</Link>
          </div>
        </div>

        {/* Credit score bonus guide */}
        <div style={s.bonusCard}>
          <span style={s.bonusIcon}>💳</span>
          <div>
            <div style={s.bonusTitle}>Also: How to Improve Your Credit Score</div>
            <div style={s.bonusSub}>Learn what affects your score, how each factor works, and get a personalized action plan.</div>
          </div>
          <Link to="/credit-score-guide" style={s.bonusBtn}>Read Guide →</Link>
        </div>

        <p style={s.legalFooter}>
          Information is for educational purposes only. Contribution limits, income thresholds, and tax rules are based on 2026 IRS guidelines and may change annually. This is not personalized financial, tax, or legal advice. Verify current rules at IRS.gov or speak with a qualified professional.
        </p>
      </div>
    </div>
  );
}

const s = {
  page: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '60px 24px', position: 'relative', overflow: 'hidden' },
  glow: { position: 'absolute', top: '-100px', left: '50%', transform: 'translateX(-50%)', width: '900px', height: '600px', background: 'radial-gradient(ellipse at center, rgba(99,102,241,0.1) 0%, transparent 65%)', pointerEvents: 'none' },
  inner: { maxWidth: '820px', margin: '0 auto', position: 'relative', zIndex: 1 },

  header: { textAlign: 'center', marginBottom: '40px' },
  eyebrow: { display: 'inline-block', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', color: '#a5b4fc', padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' },
  title: { fontSize: 'clamp(28px,5vw,48px)', fontWeight: '900', margin: '0 0 16px', letterSpacing: '-1px' },
  sub: { color: '#64748b', fontSize: '16px', lineHeight: 1.7, maxWidth: '580px', margin: '0 auto' },

  filters: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '32px', justifyContent: 'center' },
  filterBtn: { background: '#1e293b', border: '1px solid #334155', color: '#64748b', padding: '7px 16px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit' },
  filterActive: { background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)', color: '#a5b4fc', fontWeight: '700' },

  guides: { display: 'flex', flexDirection: 'column', gap: '16px' },

  card: { background: '#1e293b', borderRadius: '18px', padding: '24px', border: '1px solid #334155', cursor: 'default', transition: 'border-color 0.2s' },
  cardOpen: { border: '1px solid rgba(99,102,241,0.35)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '10px' },
  cardLeft: { display: 'flex', alignItems: 'flex-start', gap: '14px' },
  cardIcon: { fontSize: '28px', flexShrink: 0, marginTop: '2px' },
  cardTitle: { fontSize: '18px', fontWeight: '700', marginBottom: '6px', color: '#f1f5f9' },
  tag: { display: 'inline-block', border: '1px solid', padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.3px' },
  toggle: { color: '#475569', fontSize: '14px', flexShrink: 0, paddingTop: '4px' },
  cardSummary: { color: '#64748b', fontSize: '14px', lineHeight: 1.65, margin: 0 },

  // Expanded
  expanded: { marginTop: '24px', borderTop: '1px solid #334155', paddingTop: '24px' },

  infoRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '28px' },
  infoBox: { background: '#0f172a', borderRadius: '12px', padding: '16px', border: '1px solid #1e293b' },
  infoLabel: { fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  infoText: { color: '#94a3b8', fontSize: '13px', lineHeight: 1.6, margin: 0 },

  stepsSection: { marginBottom: '24px' },
  stepsTitle: { fontSize: '13px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px' },
  step: { display: 'flex', gap: '14px', marginBottom: '16px' },
  stepNum: { width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc', fontSize: '12px', fontWeight: '800', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  stepBody: { flex: 1 },
  stepTitle: { fontSize: '15px', fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' },
  stepDesc: { color: '#64748b', fontSize: '13px', lineHeight: 1.65, margin: 0 },

  keyFacts: { background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.12)', borderRadius: '12px', padding: '18px', marginBottom: '16px' },
  keyFactsTitle: { fontSize: '13px', fontWeight: '700', color: '#a5b4fc', marginBottom: '12px' },
  fact: { display: 'flex', gap: '10px', marginBottom: '8px' },
  factDot: { color: '#a5b4fc', flexShrink: 0, fontSize: '16px', lineHeight: 1.4 },
  factText: { color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 },

  disclaimer: { background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.12)', borderRadius: '10px', padding: '12px 16px', color: '#64748b', fontSize: '12px', lineHeight: 1.6, margin: '16px 0 0' },

  cta: { background: 'linear-gradient(135deg, rgba(56,189,248,0.05), rgba(99,102,241,0.05))', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginTop: '40px' },
  ctaLeft: {},
  ctaDesc: { color: '#64748b', fontSize: '14px', margin: '4px 0 0' },
  ctaBtns: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  ctaBtn: { background: '#38bdf8', color: '#0f172a', padding: '11px 22px', borderRadius: '10px', fontWeight: '700', fontSize: '14px', textDecoration: 'none' },
  ctaBtnOutline: { background: 'transparent', border: '2px solid rgba(56,189,248,0.3)', color: '#38bdf8', padding: '11px 22px', borderRadius: '10px', fontWeight: '700', fontSize: '14px', textDecoration: 'none' },

  legalFooter: { color: '#334155', fontSize: '12px', lineHeight: 1.65, textAlign: 'center', marginTop: '32px' },
  bonusCard:  { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', background: 'rgba(251,146,60,0.05)', border: '1px solid rgba(251,146,60,0.2)', borderRadius: '14px', padding: '20px 24px', marginTop: '32px' },
  bonusIcon:  { fontSize: '28px', flexShrink: 0 },
  bonusTitle: { fontSize: '15px', fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' },
  bonusSub:   { fontSize: '13px', color: '#64748b' },
  bonusBtn:   { background: '#fb923c', color: '#0f172a', padding: '10px 18px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', textDecoration: 'none', whiteSpace: 'nowrap', marginLeft: 'auto' },
};
